import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, Observable, of, switchMap, throwError } from 'rxjs';
import {
  FUNDS_REPORTING_BASE_PATH,
  FUNDSSERVICES_SECURITY_BASE_PATH,
  FUNDSSERVICES_REPORTING_BASE_PATH,
  PUBLIC_LOGIN_PATH,
  LOGOUT_REASONS,
} from 'src/app/constants/NextGenConstants';
import { ReportingConstants } from '../constants/ReportingConstants';
import { CsrfService } from '../services/csrf/csrf.service';
import { AuthService } from '../services/security/auth.service';
import { LoginService } from 'src/app/services/security/login.service';

/*
 * ReportingInterceptor intercepts HTTP requests and adds the CSRF token to the headers for state-changing requests.
 */
@Injectable()
export class ReportingInterceptor implements HttpInterceptor {
  private readonly document = inject(DOCUMENT);
  private readonly securityPoliciesPath = `${FUNDSSERVICES_SECURITY_BASE_PATH}/policies`;

  constructor(
    private readonly csrfService: CsrfService,
    private readonly authService: AuthService,
    private readonly loginService: LoginService,
    private readonly router: Router,
  ) {}

  private isReportingUrl(url?: string): boolean {
    return (
      !!url &&
      (url.includes(FUNDSSERVICES_REPORTING_BASE_PATH) ||
        url.includes(this.securityPoliciesPath))
    );
  }

  // API with JSON response and URL contains 'login' indicates session expired or invalid
  private readonly handleResponse = (event: HttpEvent<unknown>) => {
    if (
      event instanceof HttpResponse &&
      this.isReportingUrl(event.url ?? undefined) &&
      event.url?.includes(ReportingConstants.FUNDSNETWORK_LOGIN_URL)
    ) {
      this.authService.logout();
      this.router.navigate([`${FUNDS_REPORTING_BASE_PATH}/auth`]);
      return EMPTY;
    }
    return of(event);
  };

  // API with non-JSON response but URL contains 'login' or Forbidden indicates session expired or invalid
  // Example- Export API returns a blob instead of JSON
  private readonly handleError = (error: unknown) => {
    if (error instanceof HttpErrorResponse) {
      if (!this.isReportingUrl(error.url ?? undefined)) {
        return throwError(() => error);
      }

      if (error.status === 401) {
        // eJWT header was missing (session expired at AM layer) — show session timeout page.
        // Route through login with ccview=logout so the C&C DOM SDK fires onDomLogout
        // to clear the auth cookie before showing the session timeout page.
        this.authService.logout();
        this.loginService.logout();
        this.router.navigate([PUBLIC_LOGIN_PATH], {
          queryParams: { ccview: 'logout', reason: LOGOUT_REASONS[1] },
        });
        return EMPTY;
      }
      if (
        error.status === 403 ||
        error.url?.includes(ReportingConstants.FUNDSNETWORK_LOGIN_URL)
      ) {
        // Guard: if a concurrent 401 already triggered session-logout, do not override
        // the logout navigation with the error page (race condition on tab change).
        if (!this.loginService.isUserAuthenticated()) {
          return EMPTY;
        }
        this.authService.logout();
        this.loginService.logout();
        this.router.navigate([`${FUNDS_REPORTING_BASE_PATH}/error`]);
        return EMPTY;
      }
    }
    return throwError(() => error);
  };

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method.toUpperCase())) {
      return this.csrfService.getCsrfToken().pipe(
        switchMap((token) => {
          const csrfReq = req.clone({
            setHeaders: {
              'X-XSRF-TOKEN': token,
            },
          });
          return next
            .handle(csrfReq)
            .pipe(switchMap(this.handleResponse), catchError(this.handleError));
        }),
      );
    }
    return next
      .handle(req)
      .pipe(switchMap(this.handleResponse), catchError(this.handleError));
  }
}
